import React, { useEffect, useState } from "react";
import {
  PRINT_STATE,
  clientOsName,
  getLastPrinterError,
  getSavedThermalPrinter,
  launchQzTray,
  listPrinters,
  onPrinterStatusChange,
  openQzDownload,
  pickThermalPrinter,
  qzDownloadUrl,
} from "./tsc-label-printer";

/**
 * Thermal printers reachable from this PC (desktop app or QZ Tray) and the one
 * picked for label printing. `printers` is null until checked and [] when
 * none are reachable; an empty `printer` means "use the print dialog".
 * Shared by every thermal label print modal (GRN / Purchase Invoice, trays).
 */
export function useThermalPrinters(isOpen) {
  const [printers, setPrinters] = useState(null);
  const [printer, setPrinter] = useState(getSavedThermalPrinter);
  const [printState, setPrintState] = useState(null);
  const [checking, setChecking] = useState(false);
  const [launching, setLaunching] = useState(false);

  const refresh = async ({ force = false } = {}) => {
    setChecking(true);
    try {
      const { state, printers: found } = await listPrinters({ force });
      setPrintState(state);
      setPrinters(found);
      setPrinter((current) => pickThermalPrinter(found, current || getSavedThermalPrinter()));
    } finally {
      setChecking(false);
    }
  };

  const startQz = async () => {
    setLaunching(true);
    try {
      await launchQzTray();
      await refresh();
    } finally {
      setLaunching(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    refresh();
    // Reload printers as soon as QZ Tray connects (e.g. a late Allow).
    return onPrinterStatusChange((state) => {
      setPrintState(state);
      if (state === PRINT_STATE.OPEN) refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return { printers, printer, setPrinter, printState, checking, launching, refresh, startQz };
}

const noteClass =
  "w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug";

/** "Thermal Printer" picker, or why no printer is reachable and how to fix it. */
export function ThermalPrinterPicker({
  printers,
  printer,
  setPrinter,
  printState,
  checking,
  launching,
  refresh,
  startQz,
}) {
  return (
    <div className="flex flex-col items-start gap-1 w-full mt-4">
      <div className="flex justify-between w-full">
        <span className="text-[10px] uppercase tracking-wide text-secondary fw_500">
          Thermal Printer
        </span>
        <button
          type="button"
          className="text-[10px] text-primary underline disabled:opacity-50"
          disabled={checking}
          onClick={() => refresh({ force: true })}
        >
          {checking ? "Checking…" : "Refresh"}
        </button>
      </div>
      {printers?.length ? (
        <select
          className="w-full min-h-[36px] rounded-[8px] border border-D0D5DD px-2 text-xs bg-white"
          value={printer}
          onChange={(event) => setPrinter(event.target.value)}
        >
          <option value="">Print dialog</option>
          {printers.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      ) : launching ? (
        <p className={noteClass}>
          Starting QZ Tray… If the browser asks to open QZ Tray, click Open (tick "Always
          allow" to skip this next time).
        </p>
      ) : printers === null ? (
        <p className="text-[10px] text-secondary leading-snug">Looking for printers…</p>
      ) : printState === PRINT_STATE.INSECURE_PAGE ? (
        <div className={noteClass}>
          <p>
            This page is opened over http from{" "}
            <span className="fw_600">{window.location.host}</span>, so the browser blocks it
            from talking to QZ Tray on this computer.
          </p>
          <p className="mt-2">
            Open the app over https, or — if the app is served from this same computer — open
            it as{" "}
            <span className="fw_600">
              {`${window.location.protocol}//localhost${
                window.location.port ? `:${window.location.port}` : ""
              }${window.location.pathname}`}
            </span>
            , then click Refresh. The desktop app prints directly without this.
          </p>
        </div>
      ) : printState === PRINT_STATE.CONNECTING ? (
        <p className={noteClass}>
          Waiting for permission to reach QZ Tray. Click the icon at the left of the address bar
          and turn on <span className="fw_600">Apps on device</span>, then reload. If a QZ Tray
          window asks to allow this site, click Allow — it may be behind this window, check the
          taskbar. Printers load automatically.
        </p>
      ) : printState === PRINT_STATE.OPEN ? (
        <p className={noteClass}>
          No printers found. Check the TSC driver is installed, then click Refresh (and Allow if
          QZ Tray asks).
          {getLastPrinterError() && (
            <span className="mt-1 block break-words text-[9px] opacity-80">
              Details: {getLastPrinterError()}
            </span>
          )}
        </p>
      ) : (
        <div className={noteClass}>
          <p>QZ Tray is not running. Labels open in the print dialog until it is.</p>
          <button
            type="button"
            onClick={startQz}
            className="mt-2 rounded-md bg-primary px-3 py-1 text-xs text-white"
          >
            Start QZ Tray
          </button>
          <p className="mt-3 fw_600">Not installed yet? One-time setup:</p>
          <ol className="mt-1 list-decimal pl-4 space-y-1">
            <li>
              <a
                href={qzDownloadUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(event) => {
                  event.preventDefault();
                  openQzDownload();
                }}
                className="inline-block rounded-md bg-primary px-3 py-1 text-xs text-white"
              >
                Download QZ Tray
                {clientOsName() ? ` for ${clientOsName()}` : ""}
              </a>{" "}
              and run the installer.
            </li>
            <li>Click Start QZ Tray above (or open it from the Start menu).</li>
            <li>
              Click the icon at the left of the address bar and turn on{" "}
              <span className="fw_600">Apps on device</span>, then reload this page.
            </li>
            <li>Click Refresh above, and Allow this site when QZ Tray asks.</li>
          </ol>
          {getLastPrinterError() && (
            <p className="mt-2 break-words text-[9px] opacity-80">
              Details: {getLastPrinterError()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
